import React, { useState, useEffect } from 'react';
import { Download, FileText, Image as ImageIcon, File, Loader, X, ExternalLink } from 'lucide-react';

interface Attachment {
  id: string;
  filename: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  url: string;
  description?: string;
}

interface RequestAttachmentsProps {
  requestId: string;
}

const RequestAttachments: React.FC<RequestAttachmentsProps> = ({ requestId }) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<Attachment | null>(null);

  useEffect(() => {
    fetchAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const fetchAttachments = async () => {
    setLoading(true);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';
      const response = await fetch(`${apiUrl}/call-requests/${requestId}/attachments`);

      if (response.ok) {
        const data = await response.json();
        console.log('📎 Attachments received from backend:', data);
        console.log('📊 Number of attachments:', data.attachments?.length || 0);
        if (data.attachments && data.attachments.length > 0) {
          data.attachments.forEach((att: any, idx: number) => {
            console.log(`  File ${idx + 1}:`, att.filename);
            console.log(`    URL:`, att.url || '(missing)');
            console.log(`    MIME:`, att.mimeType);
          });
        }
        setAttachments(data.attachments || []);
      } else if (response.status === 404) {
        // No attachments found - this is okay
        console.log('ℹ️ No attachments found for request:', requestId);
        setAttachments([]);
      } else {
        throw new Error('Failed to load attachments');
      }
    } catch (err) {
      console.error('Error fetching attachments:', err);
      setError('Failed to load attachments');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <ImageIcon className="w-5 h-5 text-blue-600" />;
    if (mimeType === 'application/pdf') return <FileText className="w-5 h-5 text-red-600" />;
    return <File className="w-5 h-5 text-gray-600" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePreview = (attachment: Attachment) => {
    setPreviewFile(attachment);
  };

  const isImage = (mimeType: string) => mimeType.startsWith('image/');
  const isPDF = (mimeType: string) => mimeType === 'application/pdf';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader className="w-5 h-5 animate-spin text-blue-600" />
        <span className="ml-2 text-sm text-gray-600">Loading attachments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">{error}</p>
      </div>
    );
  }

  if (attachments.length === 0) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
        <File className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600">No attachments uploaded</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <div className="flex-shrink-0">
              {getFileIcon(attachment.mimeType)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {attachment.filename}
              </p>
              <p className="text-xs text-gray-600">
                {formatFileSize(attachment.fileSize)} • {formatDate(attachment.uploadedAt)}
              </p>
              {attachment.description && (
                <p className="text-xs text-gray-500 mt-1">{attachment.description}</p>
              )}
            </div>
            <div className="flex gap-2">
              {(isImage(attachment.mimeType) || isPDF(attachment.mimeType)) && (
                <button
                  onClick={() => handlePreview(attachment)}
                  className="flex-shrink-0 p-2 hover:bg-blue-200 rounded-lg transition-colors"
                  title="Preview file"
                >
                  <ExternalLink className="w-4 h-4 text-blue-600" />
                </button>
              )}
              <a
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 p-2 hover:bg-blue-200 rounded-lg transition-colors"
                title="Download file"
              >
                <Download className="w-4 h-4 text-blue-600" />
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {previewFile.filename}
                </h3>
                <p className="text-sm text-gray-600">
                  {formatFileSize(previewFile.fileSize)} • {formatDate(previewFile.uploadedAt)}
                </p>
              </div>
              <div className="flex gap-2 ml-4">
                <a
                  href={previewFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-5 h-5 text-gray-600" />
                </a>
                <a
                  href={previewFile.url}
                  download={previewFile.filename}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Download"
                >
                  <Download className="w-5 h-5 text-gray-600" />
                </a>
                <button
                  onClick={() => setPreviewFile(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4 bg-gray-50">
              {isImage(previewFile.mimeType) ? (
                <div className="flex items-center justify-center h-full">
                  {!previewFile.url || previewFile.url === '' ? (
                    <div className="text-center p-8">
                      <div className="text-red-600 mb-4">
                        <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <p className="text-lg font-semibold mb-2">No URL Available</p>
                        <p className="text-sm text-gray-600 mb-4">The backend did not provide a URL for this file</p>
                      </div>
                      <div className="text-left bg-gray-100 p-4 rounded-lg mb-4">
                        <p className="text-xs text-gray-700 mb-2"><strong>File details:</strong></p>
                        <p className="text-xs font-mono text-gray-700">Filename: {previewFile.filename}</p>
                        <p className="text-xs font-mono text-gray-700">MIME: {previewFile.mimeType}</p>
                        <p className="text-xs font-mono text-gray-700">URL: {previewFile.url || '(empty)'}</p>
                      </div>
                      <p className="text-sm text-gray-600">
                        <strong>Backend needs to provide:</strong><br/>
                        A valid presigned S3 URL in the response
                      </p>
                    </div>
                  ) : (
                    <img
                      src={previewFile.url}
                      alt={previewFile.filename}
                      className="max-w-full max-h-full object-contain"
                      onLoad={() => {
                        console.log('✅ Image loaded successfully:', previewFile.filename);
                      }}
                      onError={(e) => {
                        console.error('❌ Image load error');
                        console.error('📎 File:', previewFile.filename);
                        console.error('🔗 URL:', previewFile.url);
                        console.error('📋 MIME type:', previewFile.mimeType);
                        console.error('Event:', e);
                        alert('Failed to load image.\n\nFile: ' + previewFile.filename + '\n\nPossible causes:\n• S3 URL expired\n• CORS not configured\n• File permissions issue\n\nCheck browser console for URL details.');
                      }}
                    />
                  )}
                </div>
              ) : isPDF(previewFile.mimeType) ? (
                <iframe
                  src={previewFile.url}
                  className="w-full h-full min-h-[600px] border-0"
                  title={previewFile.filename}
                  onError={(e) => {
                    console.error('❌ PDF load error');
                    console.error('📎 File:', previewFile.filename);
                    console.error('🔗 URL:', previewFile.url);
                    console.error('Event:', e);
                    alert('Failed to load PDF. The file may have expired or you may not have permission to view it.');
                  }}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <File className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">Preview not available for this file type</p>
                    <a
                      href={previewFile.url}
                      download={previewFile.filename}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download File
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RequestAttachments;
